import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAssistant } from '@entities/assistant';
import { channelApi, channelQueryKeys } from '@entities/channel';
import { useKbOverview } from '@entities/knowledge';
import { extractErrorMessage } from '@shared/api';
import { ChBadge, radioKeys } from './chUi';
import { originsFor, siteHost } from './sites';
import './channels.css';

type T = 'telegram' | 'max' | 'widget';

const STEPS: Record<'telegram' | 'max', string[]> = {
  telegram: [
    'Откройте в Telegram бота @BotFather и отправьте ему команду /newbot.',
    'Придумайте название бота (его увидят клиенты) и адрес, который заканчивается на bot.',
    'BotFather пришлёт токен — длинную строку вида 123456789:AA… Скопируйте её.',
    'Вставьте токен ниже и нажмите «Подключить».',
  ],
  max: [
    'Зайдите на dev.max.ru под своим аккаунтом MAX.',
    'Создайте бота: название, описание и аватар.',
    'В настройках бота скопируйте токен.',
    'Вставьте токен ниже и нажмите «Подключить».',
  ],
};

const TOKEN_BAD =
  'Не получилось подключить: токен не подходит. Скопируйте его ещё раз целиком — без пробелов в начале и в конце.';

/** Ответ бэкенда про неверный токен → понятная фраза из макета; остальное — как есть. */
function connectError(e: unknown) {
  const msg = extractErrorMessage(e) || '';
  // Сбой сети или мессенджера — не «токен не подходит», а «попробуйте позже».
  if (/не отвечает|временно|network|timeout|сеть/i.test(msg))
    return msg.includes('попробуйте')
      ? msg
      : 'Мессенджер сейчас не отвечает — попробуйте через минуту.';
  if (
    /токен не подходит|невалид|не боту|unauthori|invalid token|not found|не найден бот/i.test(
      msg,
    ) &&
    !/уже подключ/i.test(msg)
  )
    return TOKEN_BAD;
  return msg || 'Не получилось подключить. Попробуйте ещё раз.';
}

/** Экран 6а «Подключить канал» (утверждён 28.10.2026). */
export function ChannelConnectPage() {
  const { id = '' } = useParams<{ id: string }>();
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const type = (
    ['telegram', 'max', 'widget'].includes(sp.get('type') ?? '') ? sp.get('type') : 'telegram'
  ) as T;
  const { data: assistant } = useAssistant(id);
  const { data: kb } = useKbOverview(id);
  const kbSite = kb?.sources.find((s) => s.type === 'website' && s.state === 'ready')?.name;

  const [token, setToken] = useState('');
  const [site, setSite] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false); // Enter до перерисовки кнопки — не второй запрос
  const siteValue = site ?? (kbSite ? siteHost(kbSite) : '');

  const pick = (t: T) => {
    setErr(null);
    if (t !== type) setToken(''); // токен Telegram к MAX не подойдёт — и наоборот
    setSp({ type: t }, { replace: true });
  };

  const done = (channelId: string) => {
    qc.invalidateQueries({ queryKey: channelQueryKeys.all });
    qc.invalidateQueries({ queryKey: ['cabinet'] });
    navigate(`/assistants/${id}/channels/${channelId}`, { replace: true });
  };

  const connectBot = async () => {
    const t = token.trim();
    if (t.length < 10) {
      setErr(TOKEN_BAD);
      return;
    }
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setErr(null);
    try {
      const c = await channelApi.create({ assistant_id: id, platform: type, bot_token: t });
      done(c.id);
    } catch (e) {
      setErr(connectError(e));
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const createWidget = async () => {
    const origins = originsFor(siteValue);
    if (!origins) {
      setErr(
        'Впишите адрес сайта, например kofeservice.ru — окно чата будет работать только на нём.',
      );
      return;
    }
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);
    setErr(null);
    try {
      const c = await channelApi.create({
        assistant_id: id,
        platform: 'widget',
        allowed_origins: origins,
        display_name: (assistant?.name || 'Ассистент').slice(0, 40),
      });
      done(c.id);
    } catch (e) {
      setErr(extractErrorMessage(e) || 'Не получилось создать окно чата. Попробуйте ещё раз.');
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  return (
    <>
      <header className="v2-topbar is-section">
        <Link to={`/assistants/${id}/channels`} className="mp-back">
          <svg
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
          Каналы
        </Link>
        <span style={{ color: '#D1D5DB' }}>/</span>
        <h1>Подключить канал</h1>
      </header>
      <div
        className="v2-content is-section"
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <div
          className="ch-types"
          role="radiogroup"
          aria-label="Куда подключить"
          onKeyDown={radioKeys}
        >
          {(
            [
              ['telegram', 'Telegram', 'Бот в мессенджере'],
              ['max', 'MAX', 'Российский мессенджер'],
              ['widget', 'Сайт', 'Окно чата на сайте'],
            ] as const
          ).map(([t, title, sub]) => (
            <button
              key={t}
              type="button"
              role="radio"
              aria-checked={type === t}
              tabIndex={type === t ? 0 : -1}
              className={type === t ? 'ch-type is-on' : 'ch-type'}
              onClick={() => pick(t)}
            >
              <ChBadge kind={t} />
              <span style={{ minWidth: 0 }}>
                <span style={{ display: 'block', fontWeight: 700 }}>{title}</span>
                <span className="v2-xs v2-muted">{sub}</span>
              </span>
            </button>
          ))}
        </div>

        {type !== 'widget' ? (
          <section className="v2-card ch-steps-card" aria-labelledby="h-steps">
            <h2 id="h-steps" className="v2-h2">
              {type === 'max'
                ? 'Как получить токен бота в MAX'
                : 'Как получить токен бота в Telegram'}
            </h2>
            <ol className="ch-steps">
              {STEPS[type].map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ol>
            <label htmlFor="ch-token" style={{ fontWeight: 600, fontSize: 13, marginTop: 4 }}>
              Токен бота
            </label>
            <div className="ch-token-row">
              <input
                id="ch-token"
                className={err ? 'st-input is-err' : 'st-input'}
                style={{ height: 42, borderRadius: 10 }}
                value={token}
                autoComplete="off"
                spellCheck={false}
                placeholder={type === 'max' ? 'Токен из dev.max.ru' : '123456789:AA…'}
                onChange={(e) => {
                  setToken(e.target.value);
                  setErr(null);
                }}
                onKeyDown={(e) => e.key === 'Enter' && connectBot()}
                aria-invalid={!!err}
                aria-describedby="ch-token-hint"
              />
              <button
                type="button"
                className="v2-btn is-primary"
                style={{ height: 42 }}
                disabled={busy}
                onClick={connectBot}
              >
                {busy ? 'Подключаем…' : 'Подключить'}
              </button>
            </div>
            {err && (
              <div role="alert" className="ch-err">
                {err}
              </div>
            )}
            <div id="ch-token-hint" className="v2-xs v2-muted">
              Токен — это пароль бота. Не пересылайте его в переписке; мы храним его в зашифрованном
              виде.
            </div>
          </section>
        ) : (
          <section className="v2-card ch-steps-card" aria-labelledby="h-site">
            <h2 id="h-site" className="v2-h2">
              Окно чата на сайте
            </h2>
            <div className="v2-small" style={{ color: 'var(--v2-text-2)' }}>
              Токен не нужен: создадим окно чата и дадим код для вставки на сайт. Внешний вид
              настроите на следующем шаге.
            </div>
            <label htmlFor="ch-site" style={{ fontWeight: 600, fontSize: 13, marginTop: 4 }}>
              Адрес сайта
            </label>
            <div className="ch-token-row">
              <input
                id="ch-site"
                className={err ? 'st-input is-err' : 'st-input'}
                style={{ height: 42, borderRadius: 10, fontFamily: 'inherit' }}
                value={siteValue}
                placeholder="kofeservice.ru"
                onChange={(e) => {
                  setSite(e.target.value);
                  setErr(null);
                }}
                onKeyDown={(e) => e.key === 'Enter' && createWidget()}
                aria-describedby="ch-site-hint"
              />
              <button
                type="button"
                className="v2-btn is-primary"
                style={{ height: 42 }}
                disabled={busy}
                onClick={createWidget}
              >
                {busy ? 'Создаём…' : 'Создать окно чата'}
              </button>
            </div>
            {err && (
              <div role="alert" className="ch-err">
                {err}
              </div>
            )}
            <div id="ch-site-hint" className="v2-xs v2-muted">
              Окно будет открываться только на этом сайте — другие сайты добавите на следующем шаге.
            </div>
          </section>
        )}
      </div>
    </>
  );
}
